<template>
  <div class="reschedule-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Reschedule booking</h5>
    </div>

    <div class="view-body">
      <!-- Private reservation indicator (read-only — matches legacy Bookings.vue:2447) -->
      <div v-if="isBuyout" class="upgrade-alert">
        <i class="tim-icons icon-check-2"></i>
        <span><strong>Private buyout.</strong> This booking bought out the whole slot; the subtotal stays locked to the original total on reschedule.</span>
      </div>

      <!-- Current booking info -->
      <div class="current-booking-info">
        <p class="current-booking-info__label">Current booking</p>
        <div class="current-booking-info__row">
          <span class="current-booking-info__key">Event</span>
          <span class="current-booking-info__value text-white">{{ currentGameName }}</span>
        </div>
        <div class="current-booking-info__row">
          <span class="current-booking-info__key">Date</span>
          <span class="current-booking-info__value text-white">{{ formattedCurrentDate }}</span>
        </div>
        <div class="current-booking-info__row">
          <span class="current-booking-info__key">Time</span>
          <span class="current-booking-info__value text-white">{{ formattedCurrentTime }}</span>
        </div>
      </div>

      <!-- Event selector -->
      <base-input label="Event">
        <el-select
          v-model="selectedGameName"
          class="select-primary"
          placeholder="Select event"
          @change="onGameChange"
        >
          <el-option
            v-for="game in games"
            class="select-primary"
            :label="game.internal_name || game.name"
            :value="game.name"
            :key="game.id"
          ></el-option>
        </el-select>
      </base-input>

      <!-- Date picker -->
      <base-input label="Date">
        <el-date-picker
          :editable="false"
          type="date"
          placeholder="Select date"
          value-format="yyyy-MM-dd"
          v-model="selectedDate"
          :format="dayOfWeek + dateFormatCal"
          :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
          @change="onDateChange"
        ></el-date-picker>
      </base-input>

      <!-- Time slot selector -->
      <base-input label="Time slot">
        <el-select
          v-model="selectedSlot"
          class="select-primary"
          value-key="id"
          placeholder="Select time"
          :loading="loadingSlots"
          :disabled="availableSlots.length === 0 && !loadingSlots"
        >
          <el-option
            v-for="(slot, index) in availableSlots"
            class="select-primary"
            :label="formatSlotLabel(slot)"
            :value="slot"
            :key="'RS' + index"
          >
            <span>{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}</span>
            <span v-if="isCurrentSlot(slot)" class="ml-1" style="color: rgba(255,255,255,0.6);">(Current)</span>
            <badge v-if="slot.status === 'blocked' && !slot.status_type_id" type="danger" class="badge-compact ml-2">Blocked</badge>
            <badge v-if="slot.status === 'call_to_book'" type="warning" class="badge-compact ml-2">Call to book</badge>
            <span v-if="slot.status === 'requestable'" class="badge badge-compact ml-2" :style="{ backgroundColor: '#FFD600', color: '#1E1E2F' }">Requestable</span>
            <span v-if="slot.status_type_id" class="badge badge-compact ml-2" :style="{ backgroundColor: slot.status_color, color: slot.status_text_color }">{{ slot.status_display_name }}</span>
          </el-option>
        </el-select>
      </base-input>
      <p v-if="!loadingSlots && selectedDate && availableSlots.length === 0" class="mt-0 mb-0" style="color: rgba(255,255,255,0.6); font-size: 12px;">
        No available slots for this date
      </p>

      <!-- Original party for reference once the destination differs. Counts
           carry over only where a category matches by id or name and the
           pricing model is unchanged; anything else starts at 0. -->
      <info-hint v-if="showOriginalBanner" variant="neutral" icon="icon-single-02" class="mt-2 mb-2">
        Rescheduling from {{ originalBreakdownLabel }}.
        <template v-if="pricingModelDiffers"> The destination uses a different pricing model, so participant counts start at 0.</template>
        <template v-else-if="hasUnmatchedCategories"> Categories without a matching name start at 0.</template>
      </info-hint>

      <!-- Multiple durations: pick the length for the destination -->
      <div v-if="showDurationPicker" class="mb-3">
        <label class="text-white d-block mb-1">Duration</label>
        <segmented-toggle variant="pills" size="sm" v-model="selectedDurationId" :options="durationOptions" @input="onDurationChange" />
      </div>

      <!-- Per-category player counts + live subtotal -->
      <template v-if="selectedSlot && workingTickets.length > 0">
        <div
          v-for="ticket in workingTickets"
          :key="'tc-' + ticket.category_id"
          class="pricing-row"
        >
          <div class="pricing-row__info">
            <div class="pricing-row__name-line">
              <span class="pricing-row__name text-white">{{ ticket.category }}</span>
            </div>
            <span class="pricing-row__rate">{{ getRate(ticket) }}</span>
          </div>
          <div class="pricing-row__qty">
            <base-input class="mb-0" label="Qty">
              <el-input-number v-model="ticket.number_of_players" :min="0" size="mini" @change="recalculate" />
            </base-input>
          </div>
          <div v-if="!isBuyout" class="pricing-row__total text-white">
            {{ getAmount(getLineTotal(ticket)) }}
          </div>
        </div>

        <!-- Private booking fee (tickets+fee upgrade): pre-tax component so the
             ticket lines + fee reconcile to the subtotal. -->
        <div v-if="isTicketsPlusFee && privateBookingFee > 0" class="summary-line">
          <span class="text-white">Private booking fee</span>
          <span class="text-white">{{ getAmount(privateBookingFee) }}</span>
        </div>

        <!-- Subtotal -->
        <div class="summary-line summary-line--total">
          <span class="text-white">Subtotal</span>
          <span class="text-white">{{ getAmount(rebuiltSubtotal) }}</span>
        </div>

        <!-- Total participants -->
        <div class="summary-line">
          <span class="text-white font-weight-bold">{{ rebuiltGroupSize }} {{ rebuiltGroupSize === 1 ? 'participant' : 'participants' }}</span>
        </div>
      </template>

      <!-- Revert link -->
      <div v-if="hasChanged" class="mt-2">
        <a href="#" class="text-primary small" @click.prevent="revertToOriginal">
          <i class="tim-icons icon-refresh-02 mr-1"></i>
          Revert to original
        </a>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="reschedule-error mt-3">
        <i class="tim-icons icon-alert-circle-exc mr-1"></i>
        {{ errorMessage }}
      </div>
    </div>

    <!-- Footer -->
    <div class="view-footer">
      <base-button size="sm" type="default" @click="$emit('back')">Cancel</base-button>
      <base-button
        size="sm"
        type="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="reschedule"
      >
        Reschedule
      </base-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { API_LOCATION } from '@/api-config';
import { Auth } from 'aws-amplify';
import { Select, Option, DatePicker, InputNumber } from 'element-ui';
import Badge from '@/components/Badge';
import currencies from '@/assets/currencies.json';
import { formatCurrency } from '@/util/currencyFormat';
import { calculateSubtotal, findPriceForPlayerCount, roundCents, applyPrivateUpgrade, filterPricingByDuration, sortDurations } from '@/util/pricing';
import { pickerDateFormat as toPickerDateFormat } from '@/plugins/dates';

export default {
  name: 'RescheduleView',

  components: {
    Badge,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [InputNumber.name]: InputNumber
  },

  props: {
    booking: { type: Object, required: true },
    drawerData: { type: Object, required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: '12h' },
    firstDayOfWeek: { type: Number, default: 0 },
    showDayOfWeek: { type: Boolean, default: false },
    currency: { type: String, default: 'USD' },
    currencySymbol: { type: String, default: '$' },
    currencyFormat: { type: Number, default: 0 },
    numberFormat: { type: Number, default: 0 }
  },

  data() {
    return {
      games: [],
      selectedGameName: '',
      selectedDate: '',
      selectedSlot: null,
      availableSlots: [],
      loadingSlots: false,
      saving: false,
      errorMessage: '',
      originalGameName: '',
      originalDate: '',
      // Slot the booking currently sits on, as it appears in the time slot
      // list: the slot itself for a private booking, the shared master for a
      // public sub-booking.
      originalSlotId: null,
      // Editable per-category player counts. Reseeded whenever selectedSlot
      // changes so quantities track the new slot's pricing categories while
      // preserving the original booking's counts where the categories match.
      workingTickets: [],
      // True when the last rebuild dropped some original count (no id or
      // name match on the destination). Drives the banner's second sentence.
      unmatchedOnBuild: false,
      //Multiple durations: the duration chosen for the destination (null = default)
      selectedDurationId: null,
      preselectedDurationId: null
    };
  },

  watch: {
    selectedSlot(newSlot) {
      this.selectedDurationId = this.preselectDurationFor(newSlot);
      // A duration-only change on the same slot is still a change worth saving
      this.preselectedDurationId = this.selectedDurationId;
      this.rebuildWorkingTickets(newSlot);
    },
    // The booking's own tickets changed underneath the view (drawer refetch
    // after an edit elsewhere). Re-seed the counts so the save never sends
    // quantities built from tickets that no longer exist. Keyed on content,
    // so a refetch returning the same tickets keeps manual quantity edits.
    originalTicketsKey() {
      if (this.selectedSlot) this.rebuildWorkingTickets(this.selectedSlot);
    }
  },

  computed: {
    // Use the shared helper so every supported group date format —
    // including 'DD MMM YYYY' and 'MMMM DD, YYYY' — maps correctly. The
    // previous hand-rolled converter silently fell back to 'MM-dd-yyyy'
    // for anything outside the four numeric formats listed.
    dateFormatCal() {
      return toPickerDateFormat(this.dateFormat);
    },
    dayOfWeek() {
      return this.showDayOfWeek ? 'ddd, ' : '';
    },
    currentGameName() {
      return this.drawerData.booking?.game_name || this.booking?.gameName || '';
    },
    formattedCurrentDate() {
      const d = this.drawerData.booking?.booking_date;
      return d ? moment(String(d).substring(0, 10), 'YYYY-MM-DD').format(this.dateFormat) : '';
    },
    // The window being moved: a party's own row on a public slot (its end can differ
    // from the master's, which spans the longest party), else the booking itself.
    currentWindow() {
      const master = this.drawerData.booking || {};
      const row = this.booking || {};
      return {
        booking_date: row.booking_date || master.booking_date,
        start_time: row.start_time || master.start_time,
        end_date: row.end_date || master.end_date,
        end_time: row.end_time || master.end_time
      };
    },
    formattedCurrentTime() {
      const w = this.currentWindow;
      if (!w.start_time) return '';
      return `${this.formatTime(w.start_time)} - ${this.formatTime(w.end_time)}`;
    },
    hasChanged() {
      if (!this.selectedSlot) return false;
      return this.selectedGameName !== this.originalGameName ||
        this.selectedDate !== this.originalDate ||
        this.selectedSlot.id !== this.originalSlotId ||
        this.rebuiltGroupSize !== this.originalGroupSize ||
        this.selectedDurationId !== this.preselectedDurationId;
    },
    canSave() {
      // Match Bookings.vue:isValidRescheduleSlot — group size must be > 0 for
      // both upgraded and non-upgraded reschedules.
      return this.selectedSlot && this.hasChanged && !this.saving && this.rebuiltGroupSize > 0;
    },

    // True when the booking was upgraded to a private (entire-slot) reservation.
    // Reschedule must preserve the upgrade and its locked subtotal.
    isUpgraded() {
      // Read from the resolved booking (currentBookingData = the child for a
      // public upgrade), NOT drawerData.booking (the master, whose flag is 0).
      return !!(this.booking && this.booking.is_reservation_upgraded);
    },
    upgradePricingMode() {
      return (this.booking && this.booking.reservation_upgrade_pricing_mode) || 'buyout';
    },
    // Buyout locks the subtotal to the flat fee (count-independent). Tickets+fee
    // recomputes per-ticket against the new slot and adds the fixed private fee.
    isBuyout() {
      return this.isUpgraded && this.upgradePricingMode === 'buyout';
    },
    isTicketsPlusFee() {
      return this.isUpgraded && this.upgradePricingMode === 'tickets_plus_fee';
    },

    selectedGameObj() {
      return this.games.find(g => g.name === this.selectedGameName) || null;
    },

    // Original slot's effective pricing model (slot override when custom,
    // game default otherwise), resolved backend-side in drawerData.pricing.
    originalPricingTypeId() {
      return Number(this.drawerData.pricing?.pricing_type_id || 1);
    },
    originalCumulative() {
      return !!this.drawerData.pricing?.cumulative_category_pricing;
    },
    // True when the destination slot prices differently from the original
    // (per person vs flat, or cumulative vs per category). Counts are not
    // carried across a model change; the banner shows the original party.
    pricingModelDiffers() {
      if (!this.selectedSlot) return false;
      return Number(this.slotPricingTypeId) !== this.originalPricingTypeId ||
        this.slotCumulative !== this.originalCumulative;
    },
    showOriginalBanner() {
      return !!this.selectedSlot && this.selectedSlot.id !== this.originalSlotId;
    },
    // Tickets of the booking being rescheduled. drawerData.tickets is the
    // unified list across master + children, so a public sub-booking must
    // keep only its own rows or it carries every party's counts to the new
    // slot. Same filter as EditTicketsView.loadTicketData.
    originalTickets() {
      const slotId = Number(this.booking?.id || this.drawerData.booking?.id);
      return (this.drawerData.tickets || []).filter(t => Number(t.booking_slot_id) === slotId);
    },
    // Content signature of originalTickets. The drawer replaces its data
    // object on every refetch, so the array identity changes even when the
    // tickets do not; the watcher compares this string instead. Sorted
    // because the drawer's tickets query has no ORDER BY, so the same rows
    // can come back in a different order.
    originalTicketsKey() {
      return this.originalTickets
        .map(t => `${t.category_id}:${t.category || ''}:${t.number_of_players || 0}`)
        .sort()
        .join('|');
    },
    // Sum of the original booking's player counts. Used to detect whether
    // the user has adjusted group size so the Save button enables. Derived
    // rather than captured at mount so a drawer refetch cannot leave it stale.
    originalGroupSize() {
      return this.originalTickets.reduce((sum, t) => sum + (t.number_of_players || 0), 0);
    },
    // Fixed private fee for a tickets+fee upgrade: booking subtotal minus the
    // original ticket prices. Independent of the selected slot and the edited
    // counts, so it stays constant on reschedule.
    privateBookingFee() {
      if (!this.isTicketsPlusFee) return 0;
      const originalTicketSubtotal = this.originalTickets
        .reduce((sum, t) => sum + parseFloat(t.price || 0), 0);
      return roundCents(parseFloat(this.booking?.price || 0) - originalTicketSubtotal);
    },
    // "10 participants (5 Adults, 5 Children)" from the original tickets.
    originalBreakdownLabel() {
      const tickets = this.originalTickets;
      const byName = new Map();
      for (const t of tickets) {
        const name = t.category || 'General';
        byName.set(name, (byName.get(name) || 0) + (t.number_of_players || 0));
      }
      const parts = [...byName.entries()]
        .filter(([, n]) => n > 0)
        .map(([name, n]) => `${n} ${name}`);
      const total = `${this.originalGroupSize} ${this.originalGroupSize === 1 ? 'participant' : 'participants'}`;
      return parts.length ? `${total} (${parts.join(', ')})` : total;
    },
    // Some original count found no destination category by id or name.
    // Captured when the tickets are rebuilt so later manual edits to the
    // quantities do not flip the note on and off.
    hasUnmatchedCategories() {
      if (!this.selectedSlot || this.pricingModelDiffers) return false;
      return this.unmatchedOnBuild;
    },

    // Pricing tiers from the selected slot — custom slot pricing if enabled,
    // otherwise the game's default pricing. Mirrors Bookings.vue:8758–8780.
    slotPricing() {
      const slot = this.selectedSlot;
      if (!slot) return [];
      return this.pricingForSlot(slot);
    },

    //Multiple durations: the destination's active durations under the custom pricing gate
    effectiveDurations() {
      const slot = this.selectedSlot;
      if (!slot) return [];
      const parse = v => { try { return typeof v === 'string' ? JSON.parse(v) : (v || []); } catch (e) { return []; } };
      const enabled = slot.is_custom_pricing_enabled ? slot.enable_multiple_durations : slot.game_enable_multiple_durations;
      if (!enabled) return [];
      return slot.is_custom_pricing_enabled ? parse(slot.slot_durations) : parse(slot.game_durations);
    },
    showDurationPicker() {
      return this.effectiveDurations.length > 1;
    },
    durationOptions() {
      return sortDurations(this.effectiveDurations)
        .map(d => ({
          value: Number(d.is_default) === 1 ? null : Number(d.id),
          label: `${d.duration_minutes} min${Number(d.is_default) === 1 ? ' (default)' : ''}`
        }));
    },

    // Custom pricing uses the slot's pricing type/cumulative; default pricing
    // uses the game's. Backend aliases the slot fields with `slot_` prefix.
    slotPricingTypeId() {
      const slot = this.selectedSlot;
      if (!slot) return 1;
      return slot.is_custom_pricing_enabled
        ? (slot.slot_pricing_type_id || slot.pricing_type_id || 1)
        : (slot.pricing_type_id || 1);
    },

    slotCumulative() {
      const slot = this.selectedSlot;
      if (!slot) return false;
      return !!(slot.is_custom_pricing_enabled
        ? slot.slot_cumulative_category_pricing
        : slot.cumulative_category_pricing);
    },

    rebuiltSubtotal() {
      // Buyout locks to the booking's flat total, independent of counts and the
      // new slot's pricing. Tickets+fee recomputes per-ticket below.
      if (this.isBuyout) return roundCents(parseFloat(this.booking?.price || 0));

      const pricing = this.slotPricing;
      // No new pricing data — keep the booking's existing total so we don't
      // accidentally zero it out for slots without a pricing array.
      if (!pricing.length || !this.workingTickets.length) {
        return parseFloat(this.booking?.price || 0);
      }

      const { subtotal } = calculateSubtotal({
        pricing,
        categories: this.workingTickets,
        cumulativePricing: this.slotCumulative,
        pricingTypeId: this.slotPricingTypeId
      });
      // tickets+fee: per-ticket (recomputed for the new slot) + the fixed fee.
      return roundCents(this.isTicketsPlusFee
        ? applyPrivateUpgrade({ baseSubtotal: subtotal, mode: 'tickets_plus_fee', fee: this.privateBookingFee })
        : subtotal);
    },

    rebuiltGroupSize() {
      return this.workingTickets.reduce((sum, t) => sum + (t.number_of_players || 0), 0);
    }
  },

  async mounted() {
    const b = this.drawerData.booking;
    this.originalGameName = b?.game_name || '';
    this.selectedGameName = this.originalGameName;
    this.originalDate = b?.booking_date ? String(b.booking_date).substring(0, 10) : '';
    this.selectedDate = this.originalDate;
    // drawerData.booking is the slot the drawer opened on: the master for a
    // public sub-booking. The time slot list only ever contains masters, so
    // a child's own id would never match its current slot.
    this.originalSlotId = b?.id || this.booking?.id;

    await this.loadGames();
    if (this.selectedDate && this.selectedGameName) {
      await this.fetchAvailableSlots();
    }
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    // Re-seed the editable counts for a slot (or clear them when none is
    // selected) and note whether any original count failed to carry.
    rebuildWorkingTickets(slot) {
      this.workingTickets = slot ? this.buildTicketsForSlot(slot) : [];
      const carried = this.workingTickets.reduce((sum, t) => sum + (t.number_of_players || 0), 0);
      this.unmatchedOnBuild = !!slot && carried < this.originalGroupSize;
      // buildTicketsForSlot seeds price from the first tier it iterates per
      // category. Re-run tier matching against the actual player count so the
      // per-person label and line total agree with calculateSubtotal.
      this.recalculate();
    },

    // Build a fresh tickets array against the given slot's pricing categories,
    // preserving the original booking's per-category player counts where the
    // category_id matches. Mirrors Bookings.vue:getNewSubtotalForBookingSlot.
    //Multiple durations: tiers for the chosen duration. The game-level feed carries every duration's rows
    //as all_pricing; the slot-level custom_pricing rows are tagged with game_duration_id.
    pricingForSlot(slot) {
      const parse = v => { try { return typeof v === 'string' ? JSON.parse(v) : (v || []); } catch (e) { return []; } };
      if (slot.is_custom_pricing_enabled) {
        return filterPricingByDuration(parse(slot.custom_pricing), this.selectedDurationId);
      }
      const all = parse(slot.all_pricing);
      if (all.length > 0) return filterPricingByDuration(all, this.selectedDurationId);
      return parse(slot.pricing);
    },
    //Keep the booking's current length when the destination offers it, else the destination's default
    preselectDurationFor(slot) {
      if (!slot) return null;
      const parse = v => { try { return typeof v === 'string' ? JSON.parse(v) : (v || []); } catch (e) { return []; } };
      const enabled = slot.is_custom_pricing_enabled ? slot.enable_multiple_durations : slot.game_enable_multiple_durations;
      if (!enabled) return null;
      const durations = slot.is_custom_pricing_enabled ? parse(slot.slot_durations) : parse(slot.game_durations);
      const b = this.currentWindow;
      const start = moment(`${String(b.booking_date || '').substring(0, 10)} ${b.start_time}`, 'YYYY-MM-DD HH:mm:ss');
      const end = moment(`${String(b.end_date || b.booking_date || '').substring(0, 10)} ${b.end_time}`, 'YYYY-MM-DD HH:mm:ss');
      const currentMinutes = start.isValid() && end.isValid() ? end.diff(start, 'minutes') : null;
      const match = currentMinutes !== null ? durations.find(d => Number(d.duration_minutes) === currentMinutes) : null;
      if (match) return Number(match.is_default) === 1 ? null : Number(match.id);
      return null;
    },
    onDurationChange() {
      if (this.selectedSlot) this.rebuildWorkingTickets(this.selectedSlot);
    },
    buildTicketsForSlot(slot) {
      const pricing = this.pricingForSlot(slot);
      const originalTickets = this.originalTickets;
      // No pricing tiers on the slot — fall back to original tickets unchanged
      // so we never silently zero out the booking.
      if (!pricing.length) {
        return originalTickets.map(t => ({ ...t }));
      }

      // Different pricing model on the destination: no count carries over.
      // The banner shows the original party for reference.
      const carryCounts = !this.pricingModelDiffersFor(slot);

      // Category ids are not stable: a pricing edit archives and re-inserts
      // the game's categories, and custom-priced slots clone their own rows.
      // Match by id first, then by normalized name, else 0.
      const playersByCategory = new Map();
      const playersByName = new Map();
      for (const t of originalTickets) {
        const existing = playersByCategory.get(t.category_id) || 0;
        playersByCategory.set(t.category_id, existing + (t.number_of_players || 0));
        const nameKey = this.normalizeCategoryName(t.category);
        if (nameKey) {
          playersByName.set(nameKey, (playersByName.get(nameKey) || 0) + (t.number_of_players || 0));
        }
      }

      const seen = new Set();
      const rebuilt = [];
      for (const p of pricing) {
        if (seen.has(p.category_id)) continue;
        seen.add(p.category_id);
        let count = 0;
        if (carryCounts) {
          if (playersByCategory.has(p.category_id)) {
            count = playersByCategory.get(p.category_id);
          } else {
            count = playersByName.get(this.normalizeCategoryName(p.category)) || 0;
          }
        }
        rebuilt.push({
          category_id: p.category_id,
          category: p.category || 'General',
          number_of_players: count,
          price: p.price,
          category_pricing: pricing.filter(x => x.category_id === p.category_id)
        });
      }

      rebuilt.sort((a, b) => a.category_id - b.category_id);
      return rebuilt;
    },

    // Strict equality after trim, lowercase, and whitespace collapse. No
    // plural stripping or fuzzy matching: a wrong guess is worse than a 0.
    normalizeCategoryName(name) {
      return String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
    },

    // Same test as the pricingModelDiffers computed, for a slot that is not
    // yet the selected one (buildTicketsForSlot runs inside the watcher).
    pricingModelDiffersFor(slot) {
      const typeId = Number(slot.is_custom_pricing_enabled
        ? (slot.slot_pricing_type_id || slot.pricing_type_id || 1)
        : (slot.pricing_type_id || 1));
      const cumulative = !!(slot.is_custom_pricing_enabled
        ? slot.slot_cumulative_category_pricing
        : slot.cumulative_category_pricing);
      return typeId !== this.originalPricingTypeId || cumulative !== this.originalCumulative;
    },

    getAmount(value) {
      return formatCurrency(value, {
        currency: this.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: this.currencyFormat,
        numberFormat: this.numberFormat
      });
    },

    // "$20.00 per person" / "$200.00 flat rate" — matches EditTicketsView pattern.
    getRate(ticket) {
      const categoryName = (ticket.category || 'person').toLowerCase();
      if (this.slotPricingTypeId === 1) {
        return `${this.getAmount(ticket.price || 0)} per ${categoryName}`;
      }
      return `${this.getAmount(ticket.price || 0)} flat rate`;
    },

    // Per-row line total: per-person × players, or flat rate (only when there
    // are players). Mirrors EditTicketsView.getLineTotal.
    getLineTotal(ticket) {
      if (!ticket || !ticket.number_of_players) return 0;
      if (this.slotPricingTypeId === 1) {
        return (ticket.price || 0) * ticket.number_of_players;
      }
      return ticket.price || 0;
    },

    // Refresh ticket.price against the tier matching the current player count.
    // Cumulative pricing uses total players across all rows; per-category uses
    // each row's count. Uses the same findPriceForPlayerCount as
    // calculateSubtotal so display and subtotal can't diverge on overlapping
    // or unordered tiers (lambda GROUP_CONCAT doesn't guarantee tier order).
    recalculate() {
      const total = this.rebuiltGroupSize;
      for (const ticket of this.workingTickets) {
        if (!ticket.category_pricing || ticket.category_pricing.length === 0) continue;
        const lookupCount = this.slotCumulative ? total : ticket.number_of_players;
        const price = findPriceForPlayerCount(ticket.category_pricing, lookupCount);
        if (price > 0) {
          ticket.price = parseFloat(price);
        }
      }
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      const fmt = this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';
      if (timeStr.includes('T') || timeStr.includes(' ')) {
        return moment(timeStr).format(fmt);
      }
      return moment(timeStr, 'HH:mm:ss').format(fmt);
    },

    formatSlotLabel(slot) {
      let label = `${this.formatTime(slot.start_time)} - ${this.formatTime(slot.end_time)}`;
      if (this.isCurrentSlot(slot)) label += ' (Current)';
      return label;
    },

    isCurrentSlot(slot) {
      return slot.id === this.originalSlotId;
    },

    async loadGames() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/games?status=active`;
        const response = await axios.get(url, config);
        this.games = response.data.games || response.data || [];
      } catch (error) {
        console.error('Failed to load games:', error);
      }
    },

    async onGameChange() {
      this.selectedSlot = null;
      this.availableSlots = [];
      if (this.selectedDate) {
        await this.fetchAvailableSlots();
      }
    },

    async onDateChange() {
      this.selectedSlot = null;
      this.availableSlots = [];
      if (this.selectedDate && this.selectedGameName) {
        await this.fetchAvailableSlots();
      }
    },

    async fetchAvailableSlots() {
      const gameObj = this.selectedGameObj;
      if (!gameObj || !this.selectedDate) return;

      this.loadingSlots = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        // Format as YYYY-MM-DD. Previously used moment().toISOString() which
        // shifts the date backwards a day in positive-offset timezones (UK BST)
        // because the local-midnight moment becomes 23:00 of the previous day in UTC.
        const dateParam = moment(String(this.selectedDate).substring(0, 10), 'YYYY-MM-DD').format('YYYY-MM-DD');
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/available?t=${dateParam}&game_id=${gameObj.id}&include_blocked=1`;
        if (this.originalSlotId) {
          url += `&booking_id=${this.originalSlotId}`;
        }
        const response = await axios.get(url, config);
        // Backend returns pricing/custom_pricing as JSON-stringified arrays.
        // Parse once here so the rebuild computeds don't have to.
        this.availableSlots = (response.data.bookings || []).map(s => ({
          ...s,
          pricing: typeof s.pricing === 'string' ? JSON.parse(s.pricing) : (s.pricing || []),
          custom_pricing: typeof s.custom_pricing === 'string' ? JSON.parse(s.custom_pricing) : (s.custom_pricing || [])
        }));

        // Auto-select current slot if on original game+date
        if (this.selectedGameName === this.originalGameName && this.selectedDate === this.originalDate) {
          const currentSlot = this.availableSlots.find(s => s.id === this.originalSlotId);
          if (currentSlot) this.selectedSlot = currentSlot;
        }
      } catch (error) {
        console.error('Failed to fetch available slots:', error);
        this.availableSlots = [];
      } finally {
        this.loadingSlots = false;
      }
    },

    revertToOriginal() {
      this.selectedGameName = this.originalGameName;
      this.selectedDate = this.originalDate;
      this.selectedSlot = null;
      this.availableSlots = [];
      this.fetchAvailableSlots();
    },

    async reschedule() {
      if (!this.canSave) return;

      this.saving = true;
      this.errorMessage = '';

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const b = this.drawerData.booking;

        const startTime = moment(this.selectedSlot.start_time, ['hh:mm A', 'HH:mm:ss', 'HH:mm']).format('HH:mm');
        const endTime = moment(this.selectedSlot.end_time, ['hh:mm A', 'HH:mm:ss', 'HH:mm']).format('HH:mm');

        // Tickets and subtotal come from the rebuild against the new slot's
        // pricing — matches Bookings.vue:6547–6549 which sends the recomputed
        // pricing_categories and this.subtotal. Group size is recomputed from
        // the rebuilt tickets so they always agree (matches line 6548).
        const data = {
          transaction: b.transaction_id,
          booking_date: String(this.selectedSlot.booking_date || this.selectedDate).substring(0, 10),
          end_date: String(this.selectedSlot.end_date || this.selectedSlot.booking_date || this.selectedDate).substring(0, 10),
          game_id: this.selectedGameObj.id,
          booking_copy: this.selectedSlot.id,
          start_time: startTime,
          end_time: endTime,
          group_size: this.rebuiltGroupSize,
          price: this.rebuiltSubtotal,
          tickets: this.workingTickets,
          selected_duration_id: this.selectedDurationId,
          // Preserve upgrade-to-private status across reschedule. Matches
          // Bookings.vue:6560 which always sends this flag.
          book_entire_slot: this.isUpgraded
        };

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.booking.id}/v2`;
        await axios.put(url, data, config);

        this.$notify({ type: 'success', message: 'Booking rescheduled', timeout: 3000 });
        this.$emit('rescheduled');
      } catch (error) {
        console.error('Reschedule failed:', error);
        this.errorMessage = error.response?.data?.message || error.response?.data || 'Failed to reschedule booking';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.reschedule-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.view-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.current-booking-info {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;

  &__label {
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 10px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  &__key {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
  }

  &__value {
    font-size: 13px;
    font-weight: 500;
  }
}

.reschedule-error {
  color: #F5365C;
  font-size: 13px;
  padding: 10px 12px;
  background: rgba(245, 54, 92, 0.1);
  border-radius: 6px;
}

.upgrade-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: rgba(29, 140, 248, 0.1);
  border-left: 3px solid #1d8cf8;
  border-radius: 6px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);

  i {
    color: #1d8cf8;
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

// Pricing rows — matches EditTicketsView for visual consistency
.pricing-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.pricing-row__info {
  flex: 1;
  min-width: 0;
}

.pricing-row__name-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pricing-row__name {
  font-size: 13px;
  font-weight: 500;
  text-transform: capitalize;
}

.pricing-row__rate {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  display: block;
  margin-top: 2px;
}

.pricing-row__qty {
  width: 110px;
}

.pricing-row__total {
  width: 80px;
  text-align: right;
  font-weight: 500;
  font-size: 13px;
  padding-top: 22px;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.summary-line--total {
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  font-size: 14px;
}

.view-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>

<style lang="scss">
.white-content {
  .reschedule-view {
    .view-header {
      border-bottom-color: #e9ecef;
    }

    .view-title {
      color: #344675;
    }

    .back-btn {
      background: #f0f2f5;
      border-color: #d1d5db;
      color: #525f7f;

      &:hover {
        background: #e2e6ea;
        color: #344675;
      }
    }

    .current-booking-info {
      background: #f8f9fa;
      border-color: #e3e3e3;

      &__label { color: #9A9A9A; }
      &__key { color: #9A9A9A; }
      &__value { color: #344675 !important; }
    }

    .upgrade-alert {
      background: rgba(29, 140, 248, 0.08);
      color: #344675;
    }

    .pricing-row {
      border-bottom-color: #e3e3e3;
    }

    .pricing-row__rate {
      color: #9A9A9A;
    }

    .summary-line {
      color: #525f7f;
    }

    .summary-line--total {
      border-top-color: #e3e3e3;
    }

    .view-footer {
      border-top-color: #e9ecef;
    }

    .text-white {
      color: #344675 !important;
    }
  }
}
</style>
